import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ClipboardList, Save, RotateCcw, Loader2, Plus, Trash2, 
  CheckCircle, Clock, XCircle, ChevronDown, ChevronUp,
  HelpCircle, Power, PowerOff, Sparkles, Check, X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESULT_OPTIONS = [
  { value: "qualified", label: "✅ Qualifiziert", color: "bg-green-100 text-green-700" },
  { value: "waitlist", label: "⏳ Nicht erreicht", color: "bg-amber-100 text-amber-700" },
  { value: "rejected", label: "❌ Abgelehnt", color: "bg-red-100 text-red-700" }
];

const QUESTION_TYPES = [
  { value: "yes_no", label: "Ja/Nein Frage" },
  { value: "age_range", label: "Alter (Bereich)" },
  { value: "text", label: "Freitext" }
];

const DEFAULT_RULES = {
  questions: [],
  responses: {
    qualified: {
      message: "Du passt super zu uns! Klicke auf 'Kontaktdaten eintragen', damit wir dich persönlich kontaktieren können.",
      show_contact_button: true,
      lead_status: "qualified"
    },
    waitlist: {
      message: "Ich nehme deine Daten gerne für unsere Warteliste auf. Klicke auf 'Kontaktdaten eintragen' - wir melden uns, sobald ein passendes Projekt verfügbar ist.",
      show_contact_button: true,
      lead_status: "waitlist"
    },
    rejected: {
      message: "Vielen Dank für dein Interesse an Hype-Factory! Aktuell passt es leider nicht, aber schau gerne später wieder vorbei.",
      show_contact_button: false,
      lead_status: "unsuitable"
    }
  },
  settings: {
    is_active: true,
    ask_questions_in_order: true,
    stop_on_rejection: true,
    collect_waitlist_data: true
  }
};

const QuestionEditor = ({ question, index, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, authToken }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [suggestionRuleIndex, setSuggestionRuleIndex] = useState(null);

  const isActive = question.is_active !== false;

  const updateQuestion = (field, value) => {
    onUpdate(index, { ...question, [field]: value });
  };

  const toggleActive = () => {
    onUpdate(index, { ...question, is_active: !isActive });
  };

  const addRule = () => {
    const newRules = [...(question.rules || []), { condition: "", result: "qualified", response: "" }];
    onUpdate(index, { ...question, rules: newRules });
  };

  const updateRule = (ruleIndex, field, value) => {
    const newRules = [...question.rules];
    newRules[ruleIndex] = { ...newRules[ruleIndex], [field]: value };
    onUpdate(index, { ...question, rules: newRules });
  };

  const deleteRule = (ruleIndex) => {
    const newRules = question.rules.filter((_, i) => i !== ruleIndex);
    onUpdate(index, { ...question, rules: newRules });
  };

  const generateAISuggestion = async (ruleIndex) => {
    const rule = question.rules[ruleIndex];
    if (!rule.condition) {
      toast.error("Bitte zuerst eine Bedingung eingeben");
      return;
    }

    setGeneratingFor(ruleIndex);
    try {
      const response = await axios.post(`${API}/admin/generate-response-suggestion`, {
        question: question.question,
        condition: rule.condition,
        result: rule.result,
        user_hint: rule.response || "freundlich und professionell"
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (response.data.success) {
        setAiSuggestion(response.data.suggestion);
        setSuggestionRuleIndex(ruleIndex);
      }
    } catch (error) {
      toast.error("Fehler beim Generieren des Vorschlags");
    } finally {
      setGeneratingFor(null);
    }
  };

  const acceptSuggestion = () => {
    if (suggestionRuleIndex !== null && aiSuggestion) {
      updateRule(suggestionRuleIndex, "response", aiSuggestion);
      setAiSuggestion(null);
      setSuggestionRuleIndex(null);
      toast.success("Vorschlag übernommen");
    }
  };

  const rejectSuggestion = () => {
    setAiSuggestion(null);
    setSuggestionRuleIndex(null);
  };

  return (
    <Card className={`bg-white border-slate-200 ${!isActive ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMoveUp(index)} disabled={isFirst}>
                <ChevronUp className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMoveDown(index)} disabled={isLast}>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="outline" className="font-mono">#{index + 1}</Badge>
                <span className="truncate max-w-[300px]">{question.question || "Neue Frage"}</span>
                {!isActive && <Badge variant="secondary" className="text-xs">Deaktiviert</Badge>}
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                {QUESTION_TYPES.find(t => t.value === question.type)?.label || "Fragetyp wählen"}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Active Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isActive}
                    onCheckedChange={toggleActive}
                    data-testid={`question-${index}-toggle`}
                  />
                  {isActive ? (
                    <Power className="w-4 h-4 text-green-600" />
                  ) : (
                    <PowerOff className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isActive ? "Frage deaktivieren" : "Frage aktivieren"}</p>
              </TooltipContent>
            </Tooltip>
            
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? "Einklappen" : "Ausklappen"}
            </Button>
            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => onDelete(index)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Question Text */}
          <div className="space-y-2">
            <Label>Frage</Label>
            <Textarea
              value={question.question || ""}
              onChange={(e) => updateQuestion("question", e.target.value)}
              placeholder="z.B. 'Wie alt bist du?'"
              className="resize-none"
              rows={2}
            />
          </div>

          {/* Question Type */}
          <div className="space-y-2">
            <Label>Fragetyp</Label>
            <Select value={question.type || "yes_no"} onValueChange={(v) => updateQuestion("type", v)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUESTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rules */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Antwort-Regeln</Label>
              <Button variant="outline" size="sm" onClick={addRule}>
                <Plus className="w-4 h-4 mr-1" />
                Regel hinzufügen
              </Button>
            </div>

            {(question.rules || []).map((rule, ruleIndex) => (
              <div key={ruleIndex} className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 w-12">Wenn:</span>
                  <Input
                    value={rule.condition}
                    onChange={(e) => updateRule(ruleIndex, "condition", e.target.value)}
                    placeholder={question.type === "age_range" ? "z.B. 18-26, 27-35, 36+" : question.type === "yes_no" ? "yes / no" : "beliebig"}
                    className="flex-1"
                  />
                  <Select value={rule.result} onValueChange={(v) => updateRule(ruleIndex, "result", v)}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESULT_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className={`px-2 py-0.5 rounded ${opt.color}`}>{opt.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => deleteRule(ruleIndex)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Response with AI suggestion */}
                <div className="flex items-start gap-2">
                  <span className="text-sm text-slate-500 w-12 pt-2">Antwort:</span>
                  <div className="flex-1 space-y-2">
                    <Textarea
                      value={rule.response}
                      onChange={(e) => updateRule(ruleIndex, "response", e.target.value)}
                      placeholder="Deine Idee eingeben oder KI-Vorschlag generieren..."
                      className="resize-none"
                      rows={2}
                    />
                    
                    {/* AI Suggestion Display */}
                    {aiSuggestion && suggestionRuleIndex === ruleIndex && (
                      <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2">
                        <div className="flex items-center gap-2 text-violet-700 text-sm font-medium">
                          <Sparkles className="w-4 h-4" />
                          KI-Vorschlag:
                        </div>
                        <p className="text-slate-700 text-sm">{aiSuggestion}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="default" onClick={acceptSuggestion} className="bg-violet-600 hover:bg-violet-700">
                            <Check className="w-4 h-4 mr-1" />
                            Übernehmen
                          </Button>
                          <Button size="sm" variant="outline" onClick={rejectSuggestion}>
                            <X className="w-4 h-4 mr-1" />
                            Verwerfen
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Generate AI Suggestion Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateAISuggestion(ruleIndex)}
                      disabled={generatingFor === ruleIndex}
                      className="text-violet-600 border-violet-200 hover:bg-violet-50"
                    >
                      {generatingFor === ruleIndex ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Generiere...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1" />
                          KI-Vorschlag
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {(!question.rules || question.rules.length === 0) && (
              <p className="text-sm text-slate-400 text-center py-4">
                Noch keine Regeln definiert. Klicke auf "Regel hinzufügen".
              </p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const QualificationRulesEditor = ({ authToken, userRole, projectId = "hypefactory" }) => {
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(projectId);

  const projects = [
    { id: "hypefactory", name: "HypeFactory", color: "#3B82F6" },
    { id: "factoryhype", name: "FactoryHype", color: "#10B981" },
    { id: "factoryhype_v2", name: "FactoryHype V2", color: "#EC4899" }
  ];

  const isGloballyActive = rules.settings?.is_active !== false;

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchRules();
  }, [currentProject]);

  const fetchRules = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/system-prompt?project_id=${currentProject}`, getAuthHeaders());
      if (response.data.qualification_rules) {
        // Ensure settings.is_active exists
        const fetchedRules = response.data.qualification_rules;
        if (!fetchedRules.settings) {
          fetchedRules.settings = { is_active: true };
        }
        if (fetchedRules.settings.is_active === undefined) {
          fetchedRules.settings.is_active = true;
        }
        setRules(fetchedRules);
      } else {
        // No rules yet for this project, use defaults
        setRules(DEFAULT_RULES);
      }
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching rules:", error);
      toast.error("Fehler beim Laden der Regeln");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/qualification-rules?project_id=${currentProject}`, rules, getAuthHeaders());
      toast.success(`${projects.find(p => p.id === currentProject)?.name || "Projekt"} Qualifizierungsregeln gespeichert!`);
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setRules(DEFAULT_RULES);
    setHasChanges(true);
    setResetDialogOpen(false);
    toast.info("Regeln zurückgesetzt. Klicke 'Speichern' um die Änderungen zu übernehmen.");
  };

  const updateRules = (newRules) => {
    setRules(newRules);
    setHasChanges(true);
  };

  const toggleGlobalActive = () => {
    const newSettings = { ...rules.settings, is_active: !isGloballyActive };
    updateRules({ ...rules, settings: newSettings });
  };

  const addQuestion = () => {
    const newQuestions = [...rules.questions, {
      id: `q_${Date.now()}`,
      question: "",
      type: "yes_no",
      is_active: true,
      rules: []
    }];
    updateRules({ ...rules, questions: newQuestions });
  };

  const updateQuestion = (index, updatedQuestion) => {
    const newQuestions = [...rules.questions];
    newQuestions[index] = updatedQuestion;
    updateRules({ ...rules, questions: newQuestions });
  };

  const deleteQuestion = (index) => {
    const newQuestions = rules.questions.filter((_, i) => i !== index);
    updateRules({ ...rules, questions: newQuestions });
  };

  const moveQuestion = (index, direction) => {
    const newQuestions = [...rules.questions];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newQuestions.length) {
      [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
      updateRules({ ...rules, questions: newQuestions });
    }
  };

  const updateResponse = (type, field, value) => {
    const newResponses = {
      ...rules.responses,
      [type]: { ...rules.responses[type], [field]: value }
    };
    updateRules({ ...rules, responses: newResponses });
  };

  const activeQuestionsCount = (rules.questions || []).filter(q => q.is_active !== false).length;

  if (userRole !== "admin") {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="py-16 text-center">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nur Admins können die Qualifizierungsregeln bearbeiten.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="qualification-rules-editor">
        {/* Project Switcher */}
        <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
          {projects.map(project => (
            <button
              key={project.id}
              onClick={() => {
                if (hasChanges) {
                  if (window.confirm("Ungespeicherte Änderungen gehen verloren. Projekt wechseln?")) {
                    setCurrentProject(project.id);
                  }
                } else {
                  setCurrentProject(project.id);
                }
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                currentProject === project.id
                  ? 'bg-white shadow-sm text-slate-900'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              style={currentProject === project.id ? {
                borderBottom: `2px solid ${project.color}`
              } : {}}
            >
              {project.name}
            </button>
          ))}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-6 h-6" style={{ color: projects.find(p => p.id === currentProject)?.color }} />
              Qualifizierungs-Regeln: {projects.find(p => p.id === currentProject)?.name}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              Definiere Fragen für {projects.find(p => p.id === currentProject)?.name || "Projekt"} und lege fest, welche Antworten zu welchem Ergebnis führen.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setResetDialogOpen(true)} className="rounded-lg">
              <RotateCcw className="w-4 h-4 mr-2" />
              Zurücksetzen
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="rounded-lg bg-gradient-to-r from-blue-600 to-violet-600"
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
          </div>
        </div>

        {/* Global Active Toggle */}
        <Card className={`border-2 ${isGloballyActive ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isGloballyActive ? (
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Power className="w-5 h-5 text-green-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                    <PowerOff className="w-5 h-5 text-slate-500" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Qualifizierungs-Regeln {isGloballyActive ? 'Aktiv' : 'Deaktiviert'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {isGloballyActive 
                      ? `${activeQuestionsCount} von ${(rules.questions || []).length} Fragen aktiv` 
                      : 'Der Chatbot verwendet nur den Basis-Prompt'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">{isGloballyActive ? 'Aktiv' : 'Aus'}</span>
                <Switch
                  checked={isGloballyActive}
                  onCheckedChange={toggleGlobalActive}
                  data-testid="global-rules-toggle"
                  className="data-[state=checked]:bg-green-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-center">
              <span className="text-sm text-slate-600 font-medium">Ergebnis-Typen:</span>
              {RESULT_OPTIONS.map(opt => (
                <Badge key={opt.value} className={`${opt.color} border-0`}>
                  {opt.label}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <div className={`space-y-4 ${!isGloballyActive ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Qualifizierungsfragen</h3>
            <Button onClick={addQuestion} className="rounded-lg" disabled={!isGloballyActive}>
              <Plus className="w-4 h-4 mr-2" />
              Frage hinzufügen
            </Button>
          </div>

          {(rules.questions || []).length === 0 ? (
            <Card className="bg-white border-slate-200 border-dashed">
              <CardContent className="py-12 text-center">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Noch keine Fragen definiert.</p>
                <Button onClick={addQuestion}>
                  <Plus className="w-4 h-4 mr-2" />
                  Erste Frage hinzufügen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {(rules.questions || []).map((question, index) => (
                <QuestionEditor
                  key={question.id || index}
                  question={question}
                  index={index}
                  onUpdate={updateQuestion}
                  onDelete={deleteQuestion}
                  onMoveUp={() => moveQuestion(index, -1)}
                  onMoveDown={() => moveQuestion(index, 1)}
                  isFirst={index === 0}
                  isLast={index === (rules.questions || []).length - 1}
                  authToken={authToken}
                />
              ))}
            </div>
          )}
        </div>

        {/* Response Messages */}
        <Card className={`bg-white border-slate-200 ${!isGloballyActive ? 'opacity-50' : ''}`}>
          <CardHeader>
            <CardTitle className="text-lg">Abschluss-Nachrichten</CardTitle>
            <CardDescription>
              Was der Bot sagt, wenn alle Fragen beantwortet wurden.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Qualified Response */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-100 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <Label className="text-green-800 font-semibold">Qualifiziert</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="w-4 h-4 text-green-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Wenn alle Antworten "qualifiziert" ergeben</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Textarea
                value={rules.responses?.qualified?.message || ""}
                onChange={(e) => updateResponse("qualified", "message", e.target.value)}
                className="bg-white"
                rows={2}
                disabled={!isGloballyActive}
              />
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rules.responses?.qualified?.show_contact_button !== false}
                    onChange={(e) => updateResponse("qualified", "show_contact_button", e.target.checked)}
                    className="rounded"
                    disabled={!isGloballyActive}
                  />
                  Kontaktformular anzeigen
                </label>
                <span className="text-slate-400">|</span>
                <span className="text-slate-600">Lead-Status: <Badge variant="outline">Qualifiziert</Badge></span>
              </div>
            </div>

            {/* Waitlist Response */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-100 space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <Label className="text-amber-800 font-semibold">Warteliste</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="w-4 h-4 text-amber-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Wenn eine Antwort "waitlist" ergibt (z.B. zu alt, gebunden)</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Textarea
                value={rules.responses?.waitlist?.message || ""}
                onChange={(e) => updateResponse("waitlist", "message", e.target.value)}
                className="bg-white"
                rows={2}
                disabled={!isGloballyActive}
              />
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rules.responses?.waitlist?.show_contact_button !== false}
                    onChange={(e) => updateResponse("waitlist", "show_contact_button", e.target.checked)}
                    className="rounded"
                    disabled={!isGloballyActive}
                  />
                  Kontaktformular anzeigen
                </label>
                <span className="text-slate-400">|</span>
                <span className="text-slate-600">Lead-Status: <Badge variant="outline">Warteliste</Badge></span>
              </div>
            </div>

            {/* Rejected Response */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-100 space-y-3">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <Label className="text-red-800 font-semibold">Abgelehnt</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="w-4 h-4 text-red-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Wenn eine Antwort "rejected" ergibt (z.B. kein Vollzeit, nicht reisebereit)</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Textarea
                value={rules.responses?.rejected?.message || ""}
                onChange={(e) => updateResponse("rejected", "message", e.target.value)}
                className="bg-white"
                rows={2}
                disabled={!isGloballyActive}
              />
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rules.responses?.rejected?.show_contact_button === true}
                    onChange={(e) => updateResponse("rejected", "show_contact_button", e.target.checked)}
                    className="rounded"
                    disabled={!isGloballyActive}
                  />
                  Kontaktformular anzeigen
                </label>
                <span className="text-slate-400">|</span>
                <span className="text-slate-600">Lead-Status: <Badge variant="outline">Ungeeignet</Badge></span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reset Dialog */}
        <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Regeln zurücksetzen?</AlertDialogTitle>
              <AlertDialogDescription>
                Alle Qualifizierungsregeln werden auf die Standardwerte zurückgesetzt. Deine aktuellen Anpassungen gehen verloren.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={handleReset} className="bg-amber-600 hover:bg-amber-700 rounded-lg">
                Zurücksetzen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default QualificationRulesEditor;
